// @flow
// Страница редактора лекала: загрузка лекала и его .prn, сохранение правки
// новым лекалом в той же модели. Исходное лекало не меняется.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import gql from 'graphql-tag';
import { sanitizeRouteParams } from 'metaup/routing/routingUtils';
import type { Routes } from 'metaup/routing/routingUtils';
import { apolloMutate, apolloQuery } from '../core/data/apolloClient';
import Loader from '../elements/Loader';
import { decodeException } from '../elements/LoadFailed';
import { PrnError, coordsOf, parsePrn, plotterTransform } from './prn/prnModel';
import VariantEditorView from './views/VariantEditorView';

const variantShape = `{
  id
  name
  deviceId
  guide
  device {
    id
    name
    brand { id name }
    variants { id name }
  }
  cut { id downloadUrl }
  preview {
    id
    fit(width: 300, height: 300) { width height }
  }
}`;

// Байты файла как есть, по символу на байт (как latin1)
function bytesToText(bytes) {
  let out = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    out += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return out;
}

// Текст файла лекала по ссылке скачивания
export async function fetchPrnText(url: string) {
  let res;
  try {
    res = await fetch(url);
  } catch (e) {
    throw new PrnError('Не удалось скачать файл лекала. Проверьте связь и обновите страницу.');
  }
  if (!res.ok) throw new PrnError(`Не удалось скачать файл лекала (ответ сервера ${res.status}).`);
  return bytesToText(new Uint8Array(await res.arrayBuffer()));
}

async function loadEditor(id) {
  const variant = await apolloQuery(gql`
    query ($id: String!) {
      getVariant(id: $id) ${variantShape}
    }
  `, { id });
  if (!variant.cut || !variant.cut.downloadUrl) throw new PrnError('У этого лекала нет файла для плоттера.');
  const doc = parsePrn(await fetchPrnText(variant.cut.downloadUrl));
  const { xy, refs } = coordsOf(doc);
  const plotter = plotterTransform(xy);
  return { variant, doc, refs, xy: plotter.xy, shifted: plotter.shifted };
}

// Новое лекало в модели: файл .prn и картинка из редактора
export async function createVariantFromEditor({
  deviceId, name, isEnabled, prn, preview, guide, notes,
}: Object) {
  try {
    return await apolloMutate(gql`
      mutation ($input: VariantCreate!) {
        createVariant(input: $input) { id name }
      }
    `, {
      input: {
        deviceId,
        name,
        cut: prn,
        preview,
        guide: guide || null,
        notes: notes.slice(0, 255),
        isEnabled,
      },
    });
  } catch (e) {
    throw new Error(decodeException(e));
  }
}

function saveCopy(variant, params) {
  return createVariantFromEditor({
    ...params,
    deviceId: variant.deviceId,
    guide: variant.guide,
    notes: `Сделано в редакторе из «${variant.name}», ${new Date().toLocaleDateString('ru-RU')}`,
  });
}

type Props = {
  id: string,
};

function VariantEditorPage({ id }: Props) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    setError(null);
    loadEditor(id)
      .then(d => { if (alive) setData(d); })
      .catch(e => { if (alive) setError(e instanceof PrnError ? e.message : decodeException(e)); });
    return () => { alive = false; };
  }, [id]);

  if (error) {
    return (
      <div className="alert alert-danger">
        <p className="mb-2">Лекало не открыть в редакторе. {error}</p>
        <Link to="/library/brands/">К брендам</Link>
      </div>
    );
  }
  if (!data) return <Loader />;

  return (
    <VariantEditorView
      key={id}
      variant={data.variant}
      doc={data.doc}
      refs={data.refs}
      initialXy={data.xy}
      shifted={data.shifted}
      onSave={async params => {
        const created = await saveCopy(data.variant, params);
        // Новое название занято: второй раз под ним не сохранить
        setData(d => ({
          ...d,
          variant: {
            ...d.variant,
            device: { ...d.variant.device, variants: d.variant.device.variants.concat([created]) },
          },
        }));
        return created;
      }}
    />
  );
}

export function variantEditorPageRoutes(): Routes {
  return [
    {
      title: 'Редактор лекала',
      path: '/variant-editor/:id/',
      isEnabled: ({ isUser }) => isUser,
      render: params => (
        <VariantEditorPage
          {...sanitizeRouteParams(params, {
            id: 'id',
          })}
        />
      ),
      design: null, // eslint-disable-line global-require
    },
  ];
}
